import type { EnvironmentProviders, Provider } from '@angular/core';
import { importProvidersFrom, inject, provideEnvironmentInitializer } from '@angular/core';
import type { Routes } from '@angular/router';
import { Router } from '@angular/router';

import { Actions, ofType, provideEffects } from '@ngrx/effects';
import { Store } from '@ngrx/store';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { CandidatesDataSourceService } from 'domains/recruitee/apps/recruitee/src/app/features/candidate/views/candidates-data-source.service';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { JobsBoardCardComponent } from 'domains/recruitee/apps/recruitee/src/app/features/offers/views/board/card/jobs-board-card.component';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { JobsGroupHeaderComponent } from 'domains/recruitee/apps/recruitee/src/app/features/offers/views/board/group-header/group-header.component';
// eslint-disable-next-line boundaries/element-types
import { ViewsOffersBulkActionsController } from 'domains/recruitee/apps/recruitee/src/app/features/offers/views/bulk-actions/views-offers-bulk-actions-controller.service';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { ViewsOffersBulkActionsExecutorService } from 'domains/recruitee/apps/recruitee/src/app/features/offers/views/bulk-actions/views-offers-bulk-actions-executor.service';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { ViewsOffersBulkActionsValidatorService } from 'domains/recruitee/apps/recruitee/src/app/features/offers/views/bulk-actions/views-offers-bulk-actions-validator.service';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { OfferListWrapperComponent } from 'domains/recruitee/apps/recruitee/src/app/features/offers/views/offer-list-item/offer-list-wrapper.component';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { JobsDataSourceService } from 'domains/recruitee/apps/recruitee/src/app/features/offers/views/services/jobs-data-source.service';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { ViewsJobsEffects } from 'domains/recruitee/apps/recruitee/src/app/features/offers/views/services/jobs.effects';
// eslint-disable-next-line boundaries/element-types -- FIXME
import type { OfferTableFields } from 'domains/recruitee/apps/recruitee/src/app/features/offers/views/table-cells/offer-columns';
/* eslint-disable boundaries/element-types */
import {
  offerColumnFieldMap,
  offerColumns,
} from 'domains/recruitee/apps/recruitee/src/app/features/offers/views/table-cells/offer-columns';
/* eslint-enable boundaries/element-types */
// eslint-disable-next-line boundaries/element-types -- FIXME
import { RtPricingFeatureConnectedModule } from 'domains/recruitee/apps/recruitee/src/app/features/pricing/connected/pricing-feature-connected.module';
import { merge, take, tap } from 'rxjs';

import { UserActions } from '@core/account-data';
import { BulkActionsController, BulkActionsSelectionTracker } from '@core/bulk-actions-legacy';
import { ViewsOffer } from '@core/offers-types';
import { RoleActions } from '@core/roles-data';
import { DataSourceActions } from '@core/views-data';
import { WrappedTableListGroupHeadingComponent } from '@core/views-ui';
import type { TableConfig } from '@core/views-util';
import {
  dataSourceProviders,
  provideBoardLayout,
  provideListLayout,
  provideTableLayout,
  provideViewBaseRoute,
} from '@core/views-util';
import { JobActionsFacade } from '@recruitee/offers-logic';

// eslint-disable-next-line boundaries/element-types -- FIXME
import { ViewsManageEeoBulkService } from '../../../../recruitee/src/app/features/offers/views/bulk-actions/views-manage-eeo-bulk.service';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { ViewsManageReferralsBulkService } from '../../../../recruitee/src/app/features/offers/views/bulk-actions/views-manage-referrals-bulk.service';
import { NewDialogComponent } from '../new-dialog/new-dialog.component';

const viewsEnvInitialize: Array<Provider | EnvironmentProviders> = [
  provideEnvironmentInitializer(() => {
    const initializerFn = (() => {
      const store = inject(Store);
      const actions$ = inject(Actions);
      const router = inject(Router);

      merge(
        actions$.pipe(
          ofType(DataSourceActions.fetchOneFailed),
          tap(() => router.navigate(['/'])),
        ),
        actions$.pipe(ofType(DataSourceActions.fetchOneSuccess)),
      )
        .pipe(take(1))
        .subscribe();

      return () => {
        store.dispatch(UserActions.fetchCollection());
        store.dispatch(RoleActions.fetchCollection());
      };
    })();
    return initializerFn();
  }),
];

const viewsProviders: Array<Provider | EnvironmentProviders> = [
  CandidatesDataSourceService,
  JobsDataSourceService,
  provideEffects(ViewsJobsEffects),
  dataSourceProviders('jobs', {
    jobs: JobsDataSourceService,
    candidates: CandidatesDataSourceService,
  }),
  provideViewBaseRoute('/views'),

  // To make offer card work
  // { provide: OfferAnalytics, useClass: JobsAnalytics },
  JobActionsFacade,
  provideListLayout({
    jobs: { header: WrappedTableListGroupHeadingComponent, record: OfferListWrapperComponent },
  }),
  provideTableLayout({
    jobs: <TableConfig<ViewsOffer, OfferTableFields>>{
      tableGroupHeader: WrappedTableListGroupHeadingComponent,
      columns: offerColumns,
      columnFieldMap: offerColumnFieldMap,
    },
  }),
  provideBoardLayout({
    jobs: {
      item: JobsBoardCardComponent,
      groupHeader: JobsGroupHeaderComponent,
    },
  }),
  importProvidersFrom(RtPricingFeatureConnectedModule),
  // Offer bulk actions services
  ViewsOffersBulkActionsController,
  { provide: BulkActionsController, useExisting: ViewsOffersBulkActionsController },
  ViewsOffersBulkActionsExecutorService,
  ViewsOffersBulkActionsValidatorService,
  ViewsManageEeoBulkService,
  ViewsManageReferralsBulkService,
  BulkActionsSelectionTracker,
];

export const ROUTES: Routes = [
  {
    path: 'prosemirror',
    loadChildren: () => import('../prosemirror/prosemirror.module').then(m => m.ProsemirrorModule),
  },
  {
    path: 'views',
    loadChildren: () =>
      import('@core/views-legacy').then(m =>
        m.provideDataSourceRouting([viewsEnvInitialize, viewsProviders], true),
      ),
  },
  {
    path: 'analytics',
    loadChildren: () => import('@recruitee/reports-smart-ui').then(m => m.REPORTS_ROUTES),
  },
  {
    path: 'job-dashboard',
    loadChildren: () =>
      import('../reports-new-job-dashboard/reports-new-job-dashboard.routing').then(
        m => m.newJobDashboardRoutes,
      ),
  },
  {
    path: 'reports-generic-grid',
    loadChildren: () =>
      import('../reports-dashboard-grid-generic/reports-dashboard-grid-generic.routing').then(
        m => m.reportsDashboardGridGenericRouting,
      ),
  },
  {
    path: 'dialog',
    component: NewDialogComponent,
  },
  {
    path: 'icons',
    loadChildren: () => import('../icons/icons.component').then(m => m.ROUTES),
  },
];
